"use client";

import { useState, useEffect } from "react";
import { useLanguage } from "../../hooks/useLanguage";
import { Calendar } from "lucide-react";
import config from "../../config/config";
import SectionHeader from "./SectionHeader";
import EventCard from "../cards/EventCard";
import PostsSkeleton from "../posts/PostsSkeleton";

export default function EventSection() {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [eventSettings, setEventSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch homepage settings for event section
        const settingsResponse = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/homepage/event`);
        const settingsData = await settingsResponse.json();

        if (settingsData.success && settingsData.data) {
          setEventSettings(settingsData.data);

          // Only fetch events if section is enabled
          if (settingsData.data.enabled !== false) {
            const eventsResponse = await fetch(
              `${config.NEXT_PUBLIC_API_URL}/api/public/p/event?limit=${settingsData.data.limit ? settingsData.data.limit : 6}`
            );

            if (eventsResponse.ok) {
              const eventsData = await eventsResponse.json();
              if (eventsData.success) {
                const events = eventsData.data.posts ? eventsData.data.posts : [];

                setUpcomingEvents(events);
              }
            }
          }
        }
      } catch (error) {

      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  const getLabel = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[language] ? field[language] : (field.en ? field.en : '');
    }
    return '';
  };



  if (loading) {
    return (
      <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6">
        <div className="max-w-7xl mx-auto w-full">
          <SectionHeader
            icon={Calendar}
            sectionTitle="Events"
            mainHeading="Upcoming Events"
          />
          <PostsSkeleton />
        </div>
      </section>
    );
  }

  // Hide section if disabled or no meaningful content
  if (eventSettings.enabled === false) {
    return null;
  }
  if (!getLabel(eventSettings.sectionTitle) && !getLabel(eventSettings.sectionSubtitle) && upcomingEvents.length === 0) {
    return null;
  }

  return (
    <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6" style={{ background: 'linear-gradient(135deg, var(--color-background) 0%, var(--color-muted) 100%)' }}>
      <div className="max-w-7xl mx-auto w-full">
        <SectionHeader
          icon={Calendar}
          sectionTitle={eventSettings.sectionTitle}
          mainHeading={eventSettings.sectionSubtitle}
          description={eventSettings.description}
          buttonText={eventSettings.viewAllButtonText}
          buttonLink={upcomingEvents.length > 0 && eventSettings.showViewAllButton !== false ? (eventSettings.viewAllButtonLink || '/events') : null}
          buttonTarget={eventSettings.viewAllButtonTarget || '_self'}
          getLabel={getLabel}
        />

        {/* Events Grid */}
        <div className="mobile-horizontal-scroll grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
          {upcomingEvents.map((event, index) => (
            <EventCard key={event._id || event.id} event={event} />
          ))}
        </div>


      </div>
    </section>
  );
}